import * as Engine from "./engine";
import * as Elements from "./elements";
import { primaryPost } from "./blog";
import { substitute } from "@common/schema/page/route";
import * as Template from "./template";
import { resolveSectionValues } from "./types";
import { defaultConfigValues } from "@common/schema/page-config";
import { BODY_ATTRIBUTES_PATTERN } from "@common/schema/page-config/fields";
// One stored file path, three contexts. @common/schema/site-file/url carries the
// argument; this module is where the base is applied, because it is the last
// place that knows both what the markup is and who is about to read it.
import { rebaseFilePaths, rebaseFileUrl, FILE_PREFIX } from "@common/schema/site-file/url";
// The shape of a page's public URL, shared with the app: the editor links an
// author to the same address this canonicalises to. See @common/schema/page/url.
import { routeUrl } from "@common/schema/page/url";

// The language a document is written in when nothing says otherwise. It is the
// literal that was hard-coded into <html lang> before locales existed, and it
// stays that value so every page written before this change composes
// identically. See localeOf below.
const DEFAULT_LOCALE = "en";

const escapeHtml = (value) =>
  String(value === undefined || value === null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// A configuration field that lands in markup as TEXT: interpolated first, and
// escaped after.
//
// The order is the whole of it. `escapeHtml` replaces & < > and " and leaves
// braces alone, so a field escaped BEFORE the interpolation pass survives it
// intact as `{{site.values.cdn}}` and is then substituted with nothing escaping
// what comes back — the escaping ran, and ran on the wrong string. Escaping
// afterwards cannot be a pass over the assembled head either, because the three
// blocks below share that string and are raw on purpose. So it is per field,
// here, and the interpolation of the head is these calls rather than one at the
// end.
//
// Interpolation is left where it is for the raw fields — `Template.interpolate`
// escapes nothing by design, which is how an inline head block reads an
// attribute and still carries a tag.
const interpolatedText = (value, context) =>
  escapeHtml(Template.interpolate(value, context));

// The three URL fields of the shell — `faviconUrl`, and every row of
// `includesStyle` and `includesScript` — allowlisted by scheme. P180.
//
// Escaping is what these had, and escaping is the wrong question for a URL: it
// keeps the value inside the attribute, which was never in doubt, and says
// nothing about what the browser will fetch when it reads it. Each of these is
// a SUBRESOURCE of the document — an icon, a stylesheet, a script tag with a
// `src` — so what they can name decides which origins run code on and style
// every page of a site.
//
// The shape is `imageSrc` in ./paragraph, and the reason it is that one rather
// than the `urlSanitizer` beside it is the note there: an anchor and a
// subresource are not the same check. `mailto:` and `tel:` are ordinary on a
// link and meaningless here; `data:` is the one that matters, because
// `<script src="data:text/javascript,…">` executes. So the answer is the narrow
// one — relative, http, https — for all three, and that is the ONE respect in
// which these three fields are the same kind. They differ everywhere else: only
// `faviconUrl` is a file reference and is rebased, and only `faviconUrl` is
// absent from the editor's frame, since `headDocument` is deliberately not part
// of `shell()`. Uniform here anyway, because the narrow answer costs none of
// them anything they are for: a favicon comes from this site's Images or from a
// CDN, and so does a stylesheet.
//
// A protocol-relative `//evil.example/x.js` has no scheme and is not relative;
// it is checked for by hand rather than being let through with `/x.js`, again
// as `imageSrc` does.
//
// "" for a refused URL, which is not a special case: the three call sites all
// drop an empty value already, for the reason each states — an empty `href` is
// the document's own URL, which is a page fetching itself as a stylesheet. So
// a refused URL costs the tag and never the document.
//
// Control characters go first, and before the scheme is read, because a browser
// strips them too: `java\nscript:` is a scheme to a browser and would not be one
// to a naive regex.
const ASSET_SCHEME = /^([a-zA-Z][a-zA-Z0-9+.\-]*):/;

const ALLOWED_ASSET_SCHEMES = new Set(["http", "https"]);

const assetUrl = (url) => {
  if (typeof url !== "string" || url.trim() === "") {
    return "";
  }

  const bare = url.replace(/[\u0000-\u0020]/g, "");
  const scheme = ASSET_SCHEME.exec(bare);

  // No scheme is a relative URL, which is what every asset picked from this
  // site's own files is — `/file/<kind>/<fileId>/content/<name>` — and what
  // `rebaseFileUrl` below needs to still be able to recognise.
  if (!scheme) {
    return bare.startsWith("//") ? "" : bare;
  }

  return ALLOWED_ASSET_SCHEMES.has(scheme[1].toLowerCase()) ? bare : "";
};

// Which rule each property of the shell merges by.
//
// Named lists rather than a switch inside the merge, and that is the whole
// reason they are up here and exported: a property missing from all four would
// fall through to the object spread in `mergeConfig`, where the page's value
// wins unconditionally — including an empty one, which is the opposite of what
// every field here means. That is a silent wrong answer on a field somebody
// adds a year from now, so it is pinned instead: the suite asserts these four
// lists together are exactly the properties /PageConfig declares, and adding a
// field without deciding how it merges fails there rather than in a document.
export const MERGE_RULES = {
  // The page's wins WHEN IT IS NON-EMPTY. Empty means "leave the site's alone",
  // which is why every property of /PageConfig/Override defaults to empty — a
  // page that overrides nothing composes exactly as its site does.
  replaced: [
    "title",
    "description",
    "faviconUrl",
    "bodyClasses",
    "bodyAttributes",
    "bodyTemplate",
  ],
  // Concatenated, SITE FIRST. A page adds a stylesheet or a meta row to its
  // site's; it does not replace the set. Order is load order for the includes
  // and document order for the metadata, so "after the site's" is the only
  // reading that does not silently reorder a site-wide asset.
  concatenated: ["includesStyle", "includesScript", "metadataList", "attributes"],
  // Joined with a newline, site first, for the same reason and with the same
  // ordering: a page's inline style has to win the cascade over its site's, and
  // last wins. HTML and CSS both degrade locally — a malformed rule is dropped
  // and the rest of the block still applies — which is what makes joining them
  // safe. Script does not; see `layered` below.
  joined: ["inlineHead", "inlineStyle"],
  // KEPT APART, and this is the one property whose merged shape is not the
  // shape either document stores. On a site and on a page `inlineScript` is a
  // string; on the merged config it is an ORDERED LIST of the non-empty
  // sources, site first, and `scripts()` emits one `<script>` per entry.
  //
  // Joining them would put two independently-authored programs in one script
  // element, and the `try/catch` that wraps it is no defence: a SyntaxError is
  // thrown when the block is PARSED, not when it runs, so the inner catch never
  // runs and a page author's typo takes the site's script down with it on that
  // page. The wrapper was written against runtime throws, which is what it
  // still handles; joining is what would make parse errors newly reachable.
  //
  // An ASI hazard is enough to cause it without anybody making a mistake: a
  // site script whose last line carries no semicolon, followed by a page script
  // whose first line opens with `(` or `[`.
  //
  // WHAT MAKES THE ASYMMETRY SAFE, and the reason to leave it alone: `configOf`'s
  // output is RENDER-ONLY. It is derived on every compose and nothing writes it
  // back — no cache, no snapshot, no denormalised copy — so the one place the
  // type differs from the stored documents is a place no stored document reads.
  // The day something persists a composed config, this is the field that would
  // round-trip as the wrong type, and the fix then is to serialise it back to a
  // string at that boundary rather than to make the merge join them again.
  layered: ["inlineScript"],
  // Merged by NAME rather than by position — see the merge itself.
  keyed: ["types", "values"],
};

// The site's shell with the page's override on top.
//
// Ported from gen-2's `mergePageConfig`
// (`archive/recovered/stack-pkg/src/libs/site-render/utils.js`), whose rules
// are the four above. Two deviations from it, both deliberate:
//
//   `attributes` was concatenated PAGE FIRST there, and the reduce that turns
//   the list into `{{attributes.x}}` keeps the last value for a repeated name —
//   so the site's won, which is the one field where gen-2 said the page loses.
//   Nothing else in that function reads that way and nothing in its own screens
//   relied on it. Site first here, so a page overrides an attribute the way it
//   overrides everything else.
//
//   `types` was not merged at all: `res` started as a copy of the site's and
//   nothing ever wrote the key, so a page declaring a slot of its own had the
//   declaration dropped and its `values` resolved against a type that was not
//   there. Merged by name here — a page's declaration replaces the site's of
//   the same name, and a new name is appended — which is what makes the
//   `values` merge mean anything.
export const mergeConfig = (site, page) => {
  const merged = { ...site, ...page };

  for (const key of MERGE_RULES.replaced) {
    merged[key] = page[key] || site[key] || "";
  }

  for (const key of MERGE_RULES.concatenated) {
    merged[key] = [...(site[key] || []), ...(page[key] || [])];
  }

  for (const key of MERGE_RULES.joined) {
    merged[key] = [site[key], page[key]].filter((part) => part).join("\n");
  }

  // A list rather than a string, unlike every other property here. See the rule.
  for (const key of MERGE_RULES.layered) {
    merged[key] = [site[key], page[key]].filter((part) => part);
  }

  // By name, page last. `types` is an array of `{ name, params }` and
  // resolveSectionValues looks a declaration up by name, so two entries sharing
  // one would make which declaration is used depend on the search order.
  const types = [...(site.types || [])];
  for (const type of page.types || []) {
    const at = types.findIndex((existing) => existing.name === type.name);
    if (at === -1) {
      types.push(type);
    } else {
      types[at] = type;
    }
  }

  merged.types = types;
  merged.values = { ...(site.values || {}), ...(page.values || {}) };

  return merged;
};

// The language this document is written in, and the one value `<html lang>`,
// every hreflang attribute and `{{locale}}` all read.
//
// DERIVED FROM THE ROWS, NOT PASSED IN, and that is what keeps the editor's
// live pane honest without anybody remembering to keep it honest. This module
// is compiled into the api bundle and the app bundle both; its three callers —
// the render Lambda, the editor's frame and the read-only preview — already
// hand it the same `page` and `site` objects. An OPTION is a thing two of them
// can disagree about. A derivation is not.
//
// The fallback chain is the "empty means inherit" convention this schema uses
// everywhere else, and its last step is load-bearing: "en" is byte-for-byte
// what `<html lang>` held before this function existed, so a page with no
// locale on a site with no default composes exactly as it did. That is the
// whole of the migration for every page written before this change.
export const localeOf = (site, page) =>
  (page && page.locale) ||
  (site && site.defaultLocale) ||
  DEFAULT_LOCALE;

// The site's translation catalog, as a lookup for one language.
//
// A flat list on the row (see @common/schema/site/fields), consulted here and
// nowhere else. Rows for other locales are skipped rather than merged, so a
// catalog can hold every language at once and cost the same to read.
//
// A row whose `name` names nothing in `values` is INERT rather than an error:
// the overlay below only replaces keys, so an orphaned translation is a typo
// that shows up as untranslated text rather than as a broken page.
const catalogOf = (site, locale) => {
  const entries = (site && site.translations) || [];
  const found = {};

  for (const entry of entries) {
    if (entry && entry.locale === locale && entry.name) {
      found[entry.name] = entry.value === undefined ? "" : entry.value;
    }
  }

  return found;
};


// The configuration a page is actually composed with: its site's, with every
// property neither of them carries filled in, and the page's own override
// merged over the top.
//
// The API answers a complete site half — `Site.config` is non-null and defaults
// through the same helper — so the defaults are for the callers that are not
// the API: a unit test composing against `{}`, and any screen rendering before
// the site query has answered. The PAGE half is defaulted for a second reason
// as well: every page written before `config` existed carries none, and there
// are rows in that state on dev.
const configOf = (site, page) => {
  const defaults = defaultConfigValues();
  const base = { ...defaults, ...((site && site.config) || {}) };

  // THE CATALOG GOES ON THE SITE'S HALF, BEFORE THE PAGE'S OVERRIDE IS MERGED,
  // and the order is the whole of the precedence.
  //
  // Three layers, least specific first: the site's own `values` are the default
  // text; this language's catalog replaces the keys it names; and the page's
  // own `config.values` — merged over the top by mergeConfig below, `keyed` —
  // still wins, which is what keeps the per-page override available for the one
  // page where a language needs different wording.
  //
  // Overlaying AFTER the merge would invert the last two and let a catalog
  // silently beat an author's deliberate per-page choice. Overlaying on the raw
  // values rather than the resolved ones is deliberate too: `resolveSectionValues`
  // walks the type declarations, so a catalog entry — always a leaf string —
  // belongs in the values it walks rather than in its output.
  const siteConfig = {
    ...base,
    values: { ...(base.values || {}), ...catalogOf(site, localeOf(site, page)) },
  };

  const pageConfig = (page && page.config) || {};
  const config = mergeConfig(siteConfig, pageConfig);

  return {
    ...config,
    // Three sources, most specific first. The page's configured `title` is the
    // document's own and wins outright; without one the page's NAME is what a
    // reader expects in the tab, which is what this did before there was a page
    // half at all; and the site's configured title is the fallback for a page
    // that somehow has neither.
    title: pageConfig.title || (page && page.title) || siteConfig.title,
    // `bodyTemplate` is `minLength: 1` on the SITE's half of the schema, so an
    // empty one cannot be saved there. A row written before that constraint
    // existed still can be, and it renders as a page with no content and
    // nothing to say why. The page's half is allowed to be empty and means
    // "use my site's", which `str` above has already resolved.
    bodyTemplate: config.bodyTemplate || defaults.bodyTemplate,
  };
};

// Every translation of this page INCLUDING THIS ONE, which is the set the tags
// below describe and the set a language switcher wants.
//
// `resolve` hands over siblings only — see alternatesOf there — because that is
// what falls out of the partition walk. Adding self happens here, once, so the
// two halves of "the group" are not each half-right in different files. It is
// also what makes a canonical self-referential and what lets a page appear in
// its own hreflang set, both of which search engines require.
const alternatesOf = (site, page, alternates, base, params, path) => {
  if (!alternates || alternates.length === 0) {
    return [];
  }

  const current = { locale: localeOf(site, page), route: page.route || "/" };

  // A sibling of a parameterised page is parameterised too, and its address
  // carries the SAME params — `/es/blog/:slug` for the `hello` the visitor is
  // reading is `/es/blog/hello`. A sibling whose pattern needs a param this
  // match did not supply has no address and is left out, rather than linked
  // with a colon in it.
  return [current, ...alternates]
    .map((alt) => ({
      alt,
      route: alt === current
        ? concreteRoute(page, { path, params })
        : substitute(alt.route || "/", params || {}),
    }))
    .filter(({ route }) => route !== null)
    .map(({ alt, route }) => ({
      locale: alt.locale || DEFAULT_LOCALE,
      route,
      url: base ? routeUrl(base, route) : "",
      current: alt === current,
    }));
};


// What a template reaches through `{{...}}` and `cxt`. Deliberately small:
// whatever goes in here becomes contract the moment somebody writes it into a
// section, and a preview that offers more than the eventual public renderer
// would teach people to use fields that will not exist.
//
// `target` is the one thing in here that is NOT contract. It says which
// composer is running — "web" for a served page, "editor" for the preview —
// and the pieces that differ between them read it rather than taking a second
// argument each. See ./elements for what it changes.
//
// `params` stands in for the path parameters a route would supply. A route is
// matched literally — there is no `/post/:slug` segment matching — so on a
// served page this is empty, and in the editor it is whatever the author typed
// on the Editor tab. Either way a template says `{{params.slug}}` and does not
// care, which is what will make the editor's copy honest the day matching
// arrives.
//
// `site` and `attributes` come from the site's configuration, and they are here
// rather than only in render() because the editor's live frame composes through
// compose() and never builds a document: a section reading `{{site.values.x}}`
// has to say the same thing in the frame as it does in the preview, or the
// preview stops being one.
// Split from the export below so that compose() can derive the configuration
// ONCE and hand the same object to both halves. Two calls to configOf would
// agree today and are one edit away from not — it merges defaults and decides
// which title wins, and the composer would then be previewing a shell it did
// not compose the context from.

// The address of the page as the visitor reached it. On a parameterised route
// that is the CONCRETE path the renderer matched — `/blog/hello`, never
// `/blog/:slug` — and the substitution is the fallback for a caller that has
// the params but not the path (the editor's frame).
const concreteRoute = (page, options = {}) =>
  options.path || substitute(page.route || "", options.params || {}) || page.route || "";

const contextOf = (config, page, options = {}) => {
  const { target = "web", params = {}, site } = options;
  const post = primaryPost(page, options.data);

  return {
    target,
    params,
    // `{{post.title}}` — the post a `blog-post` page is showing, so the page's
    // own title and description config can name it (`{{post.title}} — Blog`).
    // Null on every other page. The fields a head would want and no more; the
    // body is what the element renders.
    post: post
      ? {
        postId: post.postId || "",
        slug: post.slug || "",
        title: post.title || "",
        abstract: post.abstract || "",
        date: post.date || "",
        image: (post.image && post.image.url) || "",
      }
      : null,
    // `{{locale}}`. Top level and on `page` below, and deliberately NOT folded
    // into `params`: those are the preview parameters an AUTHOR types on the
    // Editor tab (see editorParams), so a param named `locale` would shadow the
    // document's real language and the composer would render one thing while
    // claiming another. They are also the one part of this context the
    // read-only preview does not pass and the editor's frame does, so a locale
    // that lived there would make those two screens disagree about the language
    // of one page.
    locale: localeOf(site, page),
    // `{{cxt.alternates}}` — every language this page exists in, this one
    // included. On the context and not only in the head because a tenant writes
    // their own language switcher out of it: the engine supports `for…of`, so
    // `<% for (const alt of cxt.alternates) { %>` is a switcher with no code
    // from us. Empty unless the page is in a translation group.
    alternates: alternatesOf(
      site, page, options.alternates, options.canonicalBase, params, options.path
    ),
    // Absent on the route that has no public identity to canonicalise to — see
    // the note in api/src/render.
    canonicalUrl: options.canonicalBase
      ? routeUrl(options.canonicalBase, concreteRoute(page, options))
      : "",
    // `{{page.x}}`. No `slug` any more — the field is gone from the row, and a
    // template that read it was reading the empty string on every page written
    // since routes arrived. `route` is what names a page in a URL.
    page: {
      pageId: page.pageId,
      title: page.title,
      route: page.route,
      locale: localeOf(site, page),
    },
    site: {
      // Which language this site serves where nothing says otherwise. Here so
      // that `x-default` has one definition, and readable as
      // `{{site.defaultLocale}}` by a switcher that wants to mark the default.
      defaultLocale: (site && site.defaultLocale) || "",
      // The site's own values, resolved the same way a section's are — a `list`
      // or `object` param is stored keyed by its declaration and has to be
      // walked to be readable. This is what a brand colour or a support address
      // is: one edit on the configuration screen, read from every section.
      // Already carries this language's catalog: it is overlaid on the SITE's
      // half in configOf, before the page's own override is merged over the
      // top. See the note there for why the order has to be that way round.
      values: resolveSectionValues(config),
    },
    // `{{attributes.x}}`. Flat strings the shell interpolates directly, and the
    // reason they are separate from `values`: an attribute has no type behind
    // it, which is what makes it usable from inside the head, before any type
    // has been resolved.
    attributes: (config.attributes || []).reduce((res, { name, value }) => {
      if (name) {
        res[name] = value;
      }
      return res;
    }, {}),
  };
};

export const context = (page, options = {}) =>
  contextOf(configOf(options.site, page), page, options);

// The `params` an author typed on the Editor tab, as an object a template can
// read. Stored as a list of { name, value } because that is what a form can
// edit; used as a map because that is what `{{params.x}}` needs.
export const editorParams = (page) =>
  ((page && page.editor && page.editor.params) || []).reduce((res, param) => {
    if (param && param.name) {
      res[param.name] = param.value;
    }
    return res;
  }, {});

// Compose a page and the sections it references into its parts.
//
// Split out of render() below because the editor does NOT want a document: it
// mounts the same output into a frame that already exists, so that highlighting
// and inline editing have a live DOM to attach to. Both callers must compose
// identically or the preview stops being one.
export const compose = (page, sections, options = {}) => {
  const config = configOf(options.site, page);
  const cxt = contextOf(config, page, options);
  // `editorToken` is passed as an ARGUMENT and is deliberately not in `cxt`.
  // The context is what a template reaches through `{{...}}` and `cxt.x`, so a
  // token placed there could be read by the very markup the token exists to
  // tell apart from the composer's own — `<!-- element 0 <%= cxt.editorToken %>
  // -->` would forge a live marker in one line. See ./elements.
  const parts = Elements.render(page, sections, cxt, options.editorToken, options.data);

  // `config` rides along because both callers need it and neither should derive
  // it again: render() below builds a document out of it, and the editor's
  // frame takes the shell halves that hang off it. One object, composed from
  // and previewed against.
  return { ...parts, context: cxt, config };
};

// The half of <head> that IDENTIFIES the document: title, favicon, description
// and the metadata rows.
//
// Split from the assets below because the two halves reach a document
// differently. A served page has these SPLICED into a `<head>` being written;
// the editor's live frame has a head of its own already — it is this app's
// index.html — so a second `<title>` there would be a duplicate tag describing
// the wrong document. It applies them instead, which is why the split survives
// even though the frame is no longer missing them. See shell().
//
// As VALUES rather than markup, because there are two callers and only one of
// them is building a document. `render()` below splices these into a `<head>`
// it is writing; the editor's live frame HAS a head — it is this application's
// own index.html — so it applies them to the document it already has. Deriving
// them twice is the drift this module exists to prevent, which is why this is
// one function with `headDocument` below as its markup half.
//
// Interpolated and NOT escaped. Escaping is a property of the destination:
// `headDocument` escapes each value as it splices it, and a caller setting
// `document.title` or calling `setAttribute` must not, or a title containing an
// ampersand arrives with `&amp;` in it. The ORDER is what the note on
// `interpolatedText` is about and it is unchanged — interpolate first, escape
// at the point of use.
//
// Each field is tested AFTER interpolation rather than before, so one naming a
// value the site does not carry is dropped rather than emitted empty. An empty
// `href` is the document's own URL, which is a page fetching itself.
export const identityOf = (config, context, fileBase = FILE_PREFIX) => {
  const { title, description, faviconUrl, metadataList } = config;

  const text = (value) => {
    const resolved = Template.interpolate(value, context);
    return resolved === undefined || resolved === null ? "" : String(resolved);
  };

  return {
    // The document's language, carried on `identity` rather than read off the
    // context by each caller, for the same reason `title` and `favicon` are:
    // the served document splices it into markup and the editor's frame APPLIES
    // it to a document it did not create, and those two must not be able to
    // disagree. See applyDocumentIdentity in app/src/preview/mount.
    lang: (context && context.locale) || DEFAULT_LOCALE,
    title: text(title),
    // Rebased exactly as the body's file paths are and for the same reason —
    // the field can name a value rather than a path. A favicon picked from the
    // site's own Images stores the same relative form an image on a page does,
    // so it needs the same base, and it is the ONE file reference that never
    // passes through `bodyOf`.
    //
    // Allowlisted BEFORE it is rebased and AFTER it is interpolated (P180): the
    // scheme can arrive from a site value, and `rebaseFileUrl` reads a stored
    // file path, which is a shape only a URL that survived the check can still
    // have. `assetUrl` answers "" for a refusal, which both readers of this
    // field already drop — the served page's `<link>` falls away, and the
    // editor's frame empties the icon it retargets rather than leaving the
    // EDITOR's own describing a tenant's page.
    //
    // `assetUrl` is escape-order agnostic — it strips control characters and
    // reads a scheme, none of which `escapeHtml` can create or destroy — so
    // wrapping the unescaped value here gives the identical verdict it gave
    // when it wrapped the escaped one.
    favicon: rebaseFileUrl(assetUrl(text(faviconUrl)), fileBase),
    description: text(description),
    // `type` is the ATTRIBUTE the name goes in — name, http-equiv or property.
    // An unknown one is dropped rather than emitted: the markup half splices it
    // into the tag itself, where escaping is not enough. Being an enum is also
    // what keeps it out of the interpolation above — the three literals carry
    // no `{{`.
    metadata: (metadataList || [])
      .filter((meta) => meta && ["name", "http-equiv", "property"].includes(meta.type))
      .map((meta) => ({
        type: meta.type,
        value: text(meta.value),
        content: text(meta.content),
      })),
  };
};

const headDocument = (config, context, fileBase = FILE_PREFIX) => {
  const identity = identityOf(config, context, fileBase);

  const parts = [
    // The two the editor's frame does NOT take. A charset assigned after the
    // document has been parsed changes nothing, and a viewport inside a frame
    // on a desktop editor is a second copy of a tag describing the wrong
    // viewport. Everything else in `identity` is applied there — see
    // preview/mount's applyDocumentIdentity.
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1">`,
    `<title>${escapeHtml(identity.title)}</title>`,
  ];

  if (identity.favicon) {
    parts.push(`<link rel="icon" href="${escapeHtml(identity.favicon)}">`);
  }

  if (identity.description) {
    parts.push(`<meta name="description" content="${escapeHtml(identity.description)}">`);
  }

  for (const meta of identity.metadata) {
    parts.push(
      `<meta ${meta.type}="${escapeHtml(meta.value)}" content="${escapeHtml(meta.content)}">`
    );
  }

  parts.push(...linkDocument(context));

  return parts.join("\n");
};

// WHY THIS IS NOT A `metadataList` ROW
//
// A tenant can already add arbitrary `<meta>` to the head, and the obvious
// thought is that a canonical is just one more. It is not: `metadataList` emits
// `<meta>`, and its `type` is an enum of exactly `name`, `http-equiv` and
// `property` — deliberately, because the value is spliced into the TAG where
// escaping is not a defence. A canonical and an hreflang are `<link rel=…>`
// with an `href`, which that shape cannot express and which widening the enum
// would be the wrong way to reach. So they get a closed emitter of their own,
// with no tenant-chosen attribute names in it at all.
//
// Everything here is skipped when there is no canonical base. That is the
// `/site/<orgId>/<siteId>/` route, which has no public identity to name — see
// the note on the two handlers in api/src/render. Emitting a canonical
// pointing at a *.cloudfront.net URL would tell a crawler that the
// distribution's own name is the real home of a tenant's page.
const linkDocument = (context) => {
  const { canonicalUrl, alternates, site } = context || {};

  if (!canonicalUrl) {
    return [];
  }

  // Self-referential, always. A page that names something else as canonical is
  // asking not to be indexed, which is never what a published route means.
  const parts = [`<link rel="canonical" href="${escapeHtml(canonicalUrl)}">`];

  for (const alt of alternates || []) {
    parts.push(
      `<link rel="alternate" hreflang="${escapeHtml(alt.locale)}" href="${escapeHtml(alt.url)}">`
    );
  }

  // The page a reader gets when none of the declared languages is theirs. It is
  // the site's DEFAULT locale and nothing else — if no member of the group is
  // written in it, the honest answer is to say nothing rather than to promote
  // an arbitrary translation into the role.
  const fallback = (alternates || []).find(
    (alt) => alt.locale === (site && site.defaultLocale)
  );

  if (fallback) {
    parts.push(`<link rel="alternate" hreflang="x-default" href="${escapeHtml(fallback.url)}">`);
  }

  return parts;
};

// The half of <head> that STYLES the document: the linked stylesheets, the head
// content block, the site's inline style, and last the style the page's own
// elements emitted.
//
// The order is the point and is unchanged from the single function this was
// split out of — a site's own styles win over what it links, and an element's
// win over both. Ported from the archived project's site-render/head, which
// ordered it the same way.
const headAssets = (config, elementsStyle, context) => {
  const { includesStyle, inlineHead, inlineStyle } = config;
  const parts = [];

  for (const href of includesStyle || []) {
    // Tested AFTER interpolation, so a field naming a value the site does not
    // carry is dropped rather than emitted empty. An empty `href` is the
    // document's own URL, which is a page fetching itself as a stylesheet — and
    // it is also what `assetUrl` returns for a scheme this may not link.
    const url = assetUrl(interpolatedText(href, context));
    if (url) {
      parts.push(`<link rel="stylesheet" href="${url}">`);
    }
  }

  // Raw, all three of them, and that is what they are for: a head content block
  // that was escaped could not carry a tag, and a style block that was could
  // not carry a `>`. What contains them is the preview's sandboxed iframe, the
  // same thing that contains a section's script.
  if (inlineHead) {
    parts.push(Template.interpolate(inlineHead, context));
  }

  if (inlineStyle) {
    parts.push(`<style>${Template.interpolate(inlineStyle, context)}</style>`);
  }

  if (elementsStyle) {
    parts.push(Template.interpolate(elementsStyle, context));
  }

  return parts.join("\n");
};

// Everything between <head> and </head>, for a caller building a whole document.
const head = (config, elementsStyle, context, fileBase = FILE_PREFIX) =>
  [headDocument(config, context, fileBase), headAssets(config, elementsStyle, context)]
    .filter((part) => part)
    .join("\n");

// The scripts the shell injects, after the body and before the page's own.
const scripts = (config, context) => {
  const parts = [];

  for (const src of config.includesScript || []) {
    // An include URL is a src attribute and not markup, so it is the same order
    // as the head's: interpolated, then escaped, then allowlisted. This is the
    // one of the three whose refused schemes include an executable `data:`.
    const url = assetUrl(interpolatedText(src, context));
    if (url) {
      parts.push(`<script src="${url}"></script>`);
    }
  }

  // ONE ELEMENT PER SOURCE, site first — a list here and a string in the two
  // documents it was merged from. See MERGE_RULES.layered for why they are not
  // concatenated: two programs in one script element share a parse, and a
  // SyntaxError in either takes both down before the `try` is reached.
  //
  // Raw, like the head's three: it is script, and interpolation is how it reads
  // a site value. Wrapped for the reason a section's script is — this one runs
  // on every page of the site, so one that throws on load would otherwise stop
  // every later script on every page rather than on one.
  //
  // `|| []` rather than a string fallback: every path here goes through
  // `configOf`, which merges, so a bare string would be a caller that has not.
  for (const source of config.inlineScript || []) {
    parts.push(
      `<script>\ntry {\n${Template.interpolate(source, context)}\n} catch (e) {\n  console.log(e.toString());\n}\n</script>`
    );
  }

  return parts.join("\n");
};

// The SHELL, as two blocks a caller splices into a document it already has.
//
// It exists because the editor's live frame does not build a document and so
// used to get none of this: no linked stylesheet, no site style block, no
// injected script. That is invisible on a site whose sections carry their own
// CSS and total on a site whose look comes from what the shell links — a
// classless framework styles `main` and `header` by element, so the frame
// showed unstyled markup while the preview screen beside it looked right. A
// preview that differs from the thing it previews is worse than no preview.
//
// Exported rather than reimplemented in the frame for the reason render()'s own
// note gives: two implementations of the shell is exactly the drift this module
// exists to prevent. render() below is the other caller and takes the same two
// blocks from here.
//
// `headDocument` is deliberately NOT part of this: it is markup for a caller
// writing a `<head>`, and the frame is not one. The frame takes the same values
// through `identityOf` and applies them — see the note there, and
// preview/mount's applyDocumentIdentity.
export const shell = (config, elementsStyle, context) => ({
  head: headAssets(config, elementsStyle, context),
  scripts: scripts(config, context),
});

// The three element streams, slotted into the site's body template.
//
// The template is the one shell field that can take a document down, so it is
// compiled like a section's markup and fails like one: in place, saying so,
// rather than as an exception that blanks the preview.
export const bodyOf = (config, { header, body, footer }, context, fileBase = FILE_PREFIX) => {
  let composed;
  try {
    composed = Engine.render(config.bodyTemplate, { header, content: body, footer });
  } catch (e) {
    composed = `<div style="color:#b00;font:12px monospace">Body template error: ${Engine.escapeHtml(
      e.toString()
    )}</div>`;
  }

  // `escape: true`: this pass runs over the ASSEMBLED body, so it is the one
  // that reaches every element's output whatever route that element took to get
  // here — including a `paragraph`, which never goes near a template at all.
  // Skipping the template pass is only half of avoiding interpolation, and this
  // is the half that is easy to miss.
  const interpolated = Template.interpolate(composed, context, { escape: true });

  // And the file paths, AFTER that interpolation rather than before it, for the
  // same reason the interpolation is here rather than in each element: a stored
  // path can arrive through a section template (`{{values.hero.url}}`), through
  // a paragraph's delta, or hand-written into a `code` element, and only the
  // assembled string has seen all three. Rebasing first would leave every
  // template-supplied one behind.
  //
  // Identity when nothing was passed, which is the served-on-a-tenant-hostname
  // case and the default: there the tenant's own edge inserts the hostname and
  // the stored value is already right.
  return rebaseFilePaths(interpolated, fileBase);
};

// The `<body>` tag's own class and attributes, for a caller that has a <body>
// already rather than one writing the tag.
//
// `bodyAttributes` is raw, like the inline blocks: `data-theme="dark" hidden` is
// several attributes and escaping it would emit one literal string. Unlike them
// it is CHECKED first, against the same pattern the schema now refuses a save
// on — this one is spliced into a tag, where a `>` closes the <body> early and
// everything after it lands outside the document. The schema catches that on
// write; this catches the rows written before the schema did, which no amount
// of validation upstream can reach.
//
// Dropped whole rather than escaped: escaping produces one literal attribute
// string, which is the failure this exists to avoid, and a partial splice would
// be a different tag than the author wrote.
export const bodyAttributesOf = (config) => ({
  className: config.bodyClasses || "",
  attributes:
    config.bodyAttributes &&
    new RegExp(BODY_ATTRIBUTES_PATTERN).test(config.bodyAttributes)
      ? config.bodyAttributes
      : "",
});

// Compose a page, the sections it references and the site's configuration into
// one HTML document.
//
// TWO callers now, and that is why this module lives in common/ rather than in
// the app: the preview screen composes it in the browser, and
// api/code/src/render composes the same string on the public render path. A
// difference between what an author previews and what a visitor receives can
// then only come from a difference in the INPUT — never from a second
// implementation that drifted.
//
// The shell used to be fixed, and the note here said the archived project let a
// `pageConfig` merged from the site's override the whole document — "that is
// still not ported, because nothing serves a composed page and the editor is
// the only reader". Both halves are here now: the site's configuration is the
// shell, a page's `config` is its override of it, and `mergeConfig` above is
// gen-2's `mergePageConfig` with the two corrections named there.
//
// The editor's live frame does NOT go through here — it mounts the parts into a
// document it already has, so that highlighting and inline editing have a live
// DOM to attach to — but it composes the same shell, through shell() and
// bodyOf() above, and applies the same identity through identityOf(). It used
// to share only the context and the elements, which made it a preview of the
// content rather than of the page: a site whose look comes from a linked
// stylesheet rendered unstyled in the frame and correct on the preview screen
// beside it. Then it took the shell and not the identity, so `title`,
// `faviconUrl`, `description` and the metadata rows composed on the read-only
// screens and nowhere near the Page tab they are edited on (P177).
//
// What is left of the difference is two tags and it is deliberate: `charset`,
// which does nothing once a document has been parsed, and `viewport`, which
// describes a viewport the frame does not have.
export const render = (page, sections, options = {}) => {
  const { header, body, footer, style, script, context, config } = compose(
    page,
    sections,
    options
  );

  // Where a file reference resolves for THIS document. See
  // @common/schema/site-file/url: the stored value is relative and complete
  // only where the tenant's own edge inserts the hostname, so the caller that
  // knows which context this is says so. Absent means identity, which is the
  // served-on-a-tenant-hostname case.
  const fileBase = options.fileBase || FILE_PREFIX;

  const { className, attributes } = bodyAttributesOf(config);
  const bodyClass = className ? ` class="${escapeHtml(className)}"` : "";
  const bodyAttributes = attributes ? ` ${attributes}` : "";

  return `<!doctype html>
<html lang="${escapeHtml(context.locale)}">
<head>
${head(config, style, context, fileBase)}
</head>
<body${bodyClass}${bodyAttributes}>
${bodyOf(config, { header, body, footer }, context, fileBase)}
${scripts(config, context)}
${script}
</body>
</html>`;
};

// Which sections a page needs, once each. A page can carry the same section
// twice — a rule above the fold and again below it — and fetching it twice
// would be two round trips for one row.
// Re-exported so the three places that resolve a page's sections — the editor's
// frame, the read-only preview and the render Lambda — import one module rather
// than reaching past this one into ./elements.
export { sectionUnavailable, UNAVAILABLE } from "./elements";

export const referencedSectionIds = (page) => {
  const ids = new Set();

  for (const element of (page && page.elements) || []) {
    // A section element, and only that: a blog element renders through the
    // site's own template for its kind (see ./blog), which arrives with the
    // blog data rather than with the sections.
    if (element.type === "section" && element.config && element.config.sectionId) {
      ids.add(element.config.sectionId);
    }
  }

  return Array.from(ids);
};
